import React, { useEffect, useRef, useState } from 'react'
import { DownArrowIcon } from '../../LoginPopup/icons'
import styles from './PhoneNumber.module.css'

interface Country {
  code: string
  name: string
  flag: string
  phoneCode: string
  pattern: RegExp
  placeholder: string
  maxLength: number
}

const countries: Country[] = [
  {
    code: 'UA',
    name: 'Україна',
    flag: '🇺🇦',
    phoneCode: '+380',
    pattern: /^(\+380|380)?[0-9]{9}$/,
    placeholder: 'Номер телефону',
    maxLength: 9,
  },
  {
    code: 'PL',
    name: 'Польща',
    flag: '🇵🇱',
    phoneCode: '+48',
    pattern: /^(\+48|48)?[0-9]{9}$/,
    placeholder: 'Номер телефону',
    maxLength: 9,
  },
  {
    code: 'DE',
    name: 'Німеччина',
    flag: '🇩🇪',
    phoneCode: '+49',
    pattern: /^(\+49|49)?[0-9]{10,11}$/,
    placeholder: 'Номер телефону',
    maxLength: 11,
  },
]

interface PhoneNumberProps {
  value: string
  onChange: (value: string) => void
  onCountryChange: (country: Country) => void
  selectedCountry: Country
  error?: string
  placeholder?: string
  disabled?: boolean
  label?: string
  onBlur?: () => void
  name?: string
}

const PhoneNumber: React.FC<PhoneNumberProps> = ({
  value,
  onChange,
  onCountryChange,
  selectedCountry,
  error,
  placeholder,
  disabled = false,
  label,
  onBlur,
  name,
}) => {
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false)
      }
    }

    if (isCountryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCountryDropdownOpen])

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // Дозволяємо тільки цифри
    const numericValue = inputValue.replace(/[^\d]/g, '')

    // Обмежуємо кількість символів згідно з maxLength країни
    const limitedValue = numericValue.slice(0, selectedCountry.maxLength)

    onChange(limitedValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Дозволяємо тільки цифри, Backspace, Delete, Tab, Escape, Enter, Arrow keys
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ]

    // Дозволяємо цифри (0-9)
    const isDigit = e.key >= '0' && e.key <= '9'

    // Дозволяємо всі Ctrl/Cmd комбінації (copy, paste, select all, etc.)
    const isModifierKey = (e.ctrlKey || e.metaKey) && e.key.length === 1

    if (!isDigit && !allowedKeys.includes(e.key) && !isModifierKey) {
      e.preventDefault()
    }
  }

  const handleCountrySelect = (country: Country) => {
    onCountryChange(country)
    setIsCountryDropdownOpen(false)
  }

  const handleCountryToggle = () => {
    if (!disabled) {
      setIsCountryDropdownOpen(!isCountryDropdownOpen)
    }
  }

  return (
    <div className={styles.phoneNumberContainer}>
      <div className={`${styles.inputField} ${error ? styles.errorInput : ''}`}>
        <div className={styles.fieldWrap}>
          {label && <label className={styles.fieldLabel}>{label}</label>}

          <div className={`${styles.phoneInput} ${error ? styles.phoneInputError : ''}`}>
            <div className={styles.countrySelector} ref={dropdownRef}>
              <button
                type="button"
                className={styles.countryButton}
                onClick={handleCountryToggle}
                disabled={disabled}
                aria-expanded={isCountryDropdownOpen}
                aria-haspopup="listbox"
              >
                <span className={styles.countryFlag}>{selectedCountry.flag}</span>
                <span className={styles.countryCode}>{selectedCountry.phoneCode}</span>
                <DownArrowIcon
                  className={`${styles.countryArrow} ${
                    isCountryDropdownOpen ? styles.countryArrowOpen : ''
                  }`}
                />
              </button>

              {isCountryDropdownOpen && (
                <div className={styles.countryDropdown}>
                  {countries.map(country => (
                    <button
                      key={country.code}
                      type="button"
                      className={`${styles.countryOption} ${
                        selectedCountry.code === country.code ? styles.countryOptionSelected : ''
                      }`}
                      onClick={() => handleCountrySelect(country)}
                    >
                      <span className={styles.countryOptionFlag}>{country.flag}</span>
                      <span className={styles.countryOptionCode}>{country.phoneCode}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              type="tel"
              value={value}
              onChange={handlePhoneChange}
              onKeyDown={handleKeyDown}
              onBlur={onBlur}
              className={`${styles.phoneInputField} ${error ? styles.phoneInputFieldError : ''}`}
              placeholder=" "
              disabled={disabled}
              maxLength={selectedCountry.maxLength}
              inputMode="numeric"
              pattern="[0-9]*"
              name={name}
              autoComplete="tel-national"
            />
          </div>
        </div>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}
    </div>
  )
}

export default PhoneNumber
export type { Country }
